/**
 * The duel prompt UI (Player duels, PRD #252, Slice C / ADR-0130).
 *
 * Two small DOM overlays for the front-of-flow handshake: the **challenger's
 * wager prompt** (opened when they pick a target - a numeric stake plus
 * Challenge/Cancel), and the **target's accept/decline window** (opened by an
 * inbound `DuelPrompt` - the challenger and stake, plus Accept/Decline). Both are
 * instances of the shared prompt-card primitive (`src/ui/prompt.ts`, Chassis
 * Slice H): centred modal cards in the app's warm design system, appended to
 * `document.body` as siblings of the `#app` canvas so they never interfere with
 * movement input, and plain-text (`textContent`, never `innerHTML`) so a
 * challenger's display name can never inject markup.
 *
 * The module owns only presentation and the button wiring; it holds no duel state
 * and talks to no socket - the installers pass the confirm/accept/decline
 * callbacks that send the actual commands, exactly as the wardrobe and
 * leaderboard windows are driven. At most one of the two overlays is shown at a
 * time. The `matchNoun` knob (Dice Wager, ADR-0154) weaves the match kind into
 * the copy so the dice wager reuses this whole prompt - the Prompt primitive's
 * parameterization precedent.
 */
import { createPanelButton, createPromptCard } from './ui/prompt';

/** Options for opening the challenger's wager prompt after a target is picked. */
export interface WagerPromptOptions {
  /** The picked opponent's display name, for the prompt copy. */
  opponentName: string;
  /**
   * The kind of match, woven into the prompt copy (Dice Wager, ADR-0154): a duel reads
   * "Challenge NAME to a duel to the death", a dice wager "Challenge NAME to a dice
   * wager". Defaults to the duel noun so existing callers are unchanged - the one knob
   * that lets the dice wager reuse this whole prompt.
   */
  matchNoun?: string;
  /** The challenger's spendable (durable) balance; the stake is clamped to it. */
  maxGold: number;
  /** Whether this session may stake gold (reserved). Anonymous => honor duel only (0). */
  canStake: boolean;
  /** Send the challenge with the chosen (validated, clamped) wager. */
  onConfirm: (wager: number, choice?: number) => void;
  /** Optional named choices shown above the buttons (used by card-duel deck selection). */
  choices?: readonly string[];
  /** Label for the optional choice control. */
  choiceLabel?: string;
  /** Initially selected choice, when the caller has a persisted preference. */
  initialChoice?: number;
  /** Optional: the player cancelled the prompt without challenging. */
  onCancel?: () => void;
}

/** Options for showing the target's accept/decline window. */
export interface ChallengeWindowOptions {
  /** The challenger's display name, named in the prompt. */
  challengerName: string;
  /**
   * The kind of match, woven into the prompt copy (Dice Wager, ADR-0154): "has
   * challenged you to a duel to the death" vs "to a dice wager". Defaults to the duel
   * noun so existing callers are unchanged.
   */
  matchNoun?: string;
  /** The stake in gp (`0` = an honor duel). */
  wager: number;
  /** The target accepted the challenge. */
  onAccept: (choice?: number) => void;
  /** Optional named choices shown before accepting (used by card-duel deck selection). */
  choices?: readonly string[];
  /** Label for the optional choice control. */
  choiceLabel?: string;
  /** Initially selected choice, when the caller has a persisted preference. */
  initialChoice?: number;
  /** The target declined the challenge. */
  onDecline: () => void;
}

/** The handle the duel/dice installers drive the duel UI through. */
export interface DuelUi {
  /** Open the challenger's wager prompt for a freshly-picked opponent. */
  promptWager(opts: WagerPromptOptions): void;
  /** Show the target's accept/decline window (naming the challenger and stake). */
  showChallenge(opts: ChallengeWindowOptions): void;
  /** Dismiss the accept/decline window (the challenge was withdrawn/expired/resolved). */
  dismissChallenge(): void;
  /** Whether the accept/decline window is currently shown. */
  isChallengeOpen(): boolean;
  /** Whether the challenger's wager prompt is currently shown. */
  isWagerOpen(): boolean;
}

/**
 * Build the duel prompt overlays and return the handle. `doc` is injectable so a
 * test can drive it against a constructed document; production passes the real one.
 */
export function initDuelUi(doc: Document = document): DuelUi {
  // ---- Challenger's wager prompt --------------------------------------------
  const wagerCard = createPromptCard(doc);

  const wagerTitle = doc.createElement('div');
  wagerTitle.style.marginBottom = '12px';
  wagerTitle.style.paddingBottom = '12px';
  wagerTitle.style.borderBottom = '1px solid #2c2921';
  wagerTitle.style.font = "600 14px/1.25 Georgia, 'Times New Roman', serif";
  wagerTitle.style.fontVariant = 'small-caps';
  wagerTitle.style.letterSpacing = '0.5px';
  // The gold heading colour of the app's card titles (index.html #whatsnew-title).
  wagerTitle.style.color = '#d8c690';

  const wagerRow = doc.createElement('div');
  wagerRow.style.margin = '4px 0 6px';
  const wagerLabel = doc.createElement('label');
  wagerLabel.textContent = 'Wager (gp): ';
  const wagerInput = doc.createElement('input');
  wagerInput.type = 'number';
  wagerInput.min = '0';
  wagerInput.step = '1';
  Object.assign(wagerInput.style, {
    width: '90px',
    padding: '4px 6px',
    borderRadius: '0',
    border: '1px solid #3c372c',
    background: 'rgba(5, 6, 5, 0.78)',
    color: '#cdbf8f',
    font: 'inherit',
  } as Partial<CSSStyleDeclaration>);
  wagerLabel.appendChild(wagerInput);
  wagerRow.appendChild(wagerLabel);

  const wagerNote = doc.createElement('div');
  Object.assign(wagerNote.style, {
    margin: '2px 0 12px',
    fontSize: '12px',
    // The muted secondary text of the app's cards (index.html #whatsnew-date).
    color: '#9a917c',
  } as Partial<CSSStyleDeclaration>);

  const wagerChoice = makeChoiceRow(doc);

  const wagerButtons = doc.createElement('div');
  const challengeBtn = createPanelButton(doc, 'Challenge', 'primary');
  const wagerCancelBtn = createPanelButton(doc, 'Cancel', 'plain');
  wagerButtons.append(challengeBtn, wagerCancelBtn);

  wagerCard.root.append(wagerTitle, wagerRow, wagerNote, wagerChoice.row, wagerButtons);

  // ---- Target's accept/decline window ---------------------------------------
  const challengeCard = createPromptCard(doc);

  const challengeText = doc.createElement('div');
  challengeText.style.marginBottom = '14px';

  const challengeChoice = makeChoiceRow(doc);

  const challengeButtons = doc.createElement('div');
  const acceptBtn = createPanelButton(doc, 'Accept', 'primary');
  const declineBtn = createPanelButton(doc, 'Decline', 'plain');
  challengeButtons.append(acceptBtn, declineBtn);

  challengeCard.root.append(challengeText, challengeChoice.row, challengeButtons);

  // ---- Live callbacks (rebound on each open) --------------------------------
  let confirmWager: ((wager: number, choice?: number) => void) | null = null;
  let cancelWager: (() => void) | null = null;
  let acceptChallenge: ((choice?: number) => void) | null = null;
  let declineChallenge: (() => void) | null = null;

  const closeWager = (): void => {
    wagerCard.hide();
    confirmWager = null;
    cancelWager = null;
  };
  const closeChallenge = (): void => {
    challengeCard.hide();
    acceptChallenge = null;
    declineChallenge = null;
  };

  challengeBtn.addEventListener('click', () => {
    const confirm = confirmWager;
    // Clamp to a non-negative integer within the spendable balance; a blank/NaN
    // input reads as 0 (an honor duel), so confirming always sends a clean wager.
    const raw = Math.floor(Number(wagerInput.value));
    const max = Number(wagerInput.max) || 0;
    const wager = Number.isFinite(raw) ? Math.max(0, Math.min(max, raw)) : 0;
    closeWager();
    confirm?.(wager, wagerChoice.selected());
  });
  wagerCancelBtn.addEventListener('click', () => {
    const cancel = cancelWager;
    closeWager();
    cancel?.();
  });
  acceptBtn.addEventListener('click', () => {
    const accept = acceptChallenge;
    closeChallenge();
    accept?.(challengeChoice.selected());
  });
  declineBtn.addEventListener('click', () => {
    const decline = declineChallenge;
    closeChallenge();
    decline?.();
  });

  return {
    promptWager(opts: WagerPromptOptions): void {
      closeChallenge();
      confirmWager = opts.onConfirm;
      cancelWager = opts.onCancel ?? null;
      wagerTitle.textContent = `Challenge ${opts.opponentName} to ${opts.matchNoun ?? 'a duel to the death'}`;
      const max = opts.canStake ? Math.max(0, Math.floor(opts.maxGold)) : 0;
      wagerInput.max = String(max);
      wagerInput.value = '0';
      wagerInput.disabled = !opts.canStake;
      wagerNote.textContent = opts.canStake
        ? `You may stake up to ${max} gp. Zero is an honor duel.`
        : 'Reserve a name to stake gold — anonymous duels are honor duels (0 gp).';
      wagerChoice.set(opts.choiceLabel, opts.choices, opts.initialChoice);
      wagerCard.show();
      if (opts.canStake) wagerInput.focus();
    },
    showChallenge(opts: ChallengeWindowOptions): void {
      closeWager();
      acceptChallenge = opts.onAccept;
      declineChallenge = opts.onDecline;
      const stake = opts.wager > 0 ? `for ${opts.wager} gp` : 'for no wager';
      challengeText.textContent = `${opts.challengerName} has challenged you to ${opts.matchNoun ?? 'a duel to the death'} ${stake}. Do you accept?`;
      challengeChoice.set(opts.choiceLabel, opts.choices, opts.initialChoice);
      challengeCard.show();
    },
    dismissChallenge(): void {
      closeChallenge();
    },
    isChallengeOpen(): boolean {
      return challengeCard.isOpen();
    },
    isWagerOpen(): boolean {
      return wagerCard.isOpen();
    },
  };
}

function makeChoiceRow(doc: Document): {
  row: HTMLDivElement;
  set(label: string | undefined, choices: readonly string[] | undefined, initialChoice?: number): void;
  selected(): number | undefined;
} {
  const row = doc.createElement('div');
  row.style.margin = '4px 0 14px';
  const label = doc.createElement('label');
  const select = doc.createElement('select');
  select.style.marginLeft = '6px';
  label.append(select);
  row.append(label);
  return {
    row,
    set(text, choices, initialChoice): void {
      select.replaceChildren();
      label.textContent = `${text ?? 'Choice'}: `;
      label.append(select);
      for (const [index, choice] of (choices ?? []).entries()) {
        const option = doc.createElement('option');
        option.value = String(index);
        option.textContent = choice;
        select.append(option);
      }
      if (initialChoice !== undefined && choices?.[initialChoice] !== undefined) {
        select.value = String(initialChoice);
      }
      row.hidden = !choices?.length;
    },
    selected: () => row.hidden ? undefined : Number(select.value),
  };
}
